import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import { COCKPIT_CSS } from '../cockpit/cockpitStyles';
import { CRM_CSS } from './crmStyles';
import { useCockpitDb } from '../cockpit/CockpitDataContext';
import { toast } from '../../hooks/use-toast';
import {
  TEAM_STATUS, statusOf,
  comptes, compteOf, entitesOf, equipesOf, equipesOfCompte,
  fichesOf, fichesOfTeam, initials, fullName,
} from './crmData';

const notify = (msg) => toast({ title: msg });

// Compteurs d'un compte : ce qui distingue un compte travaillé d'un compte dormant.
const statsOf = (db, compte) => {
  const eqs = equipesOfCompte(compte);
  return {
    entites: entitesOf(compte).length,
    equipes: eqs.length,
    actives: eqs.filter(({ eq }) => statusOf(eq) === 'actif').length,
    fiches: fichesOf(db, compte.name).length,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Comptes clients — la liste des grands comptes (/crm/comptes) et la fiche
// compte (/crm/comptes/:account), qui rassemble l'identité du compte, son
// périmètre (entités > équipes) et les contacts déjà référencés.
// ─────────────────────────────────────────────────────────────────────────────

export default function CrmComptes() {
  const { db, update } = useCockpitDb();
  const navigate = useNavigate();
  const { account: accountParam } = useParams();
  const account = accountParam ? decodeURIComponent(accountParam) : null;

  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', sector: '', city: '' });

  const list = comptes(db);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter((c) =>
      [c.name, c.sector, c.city].filter(Boolean).some((v) => String(v).toLowerCase().includes(s))
    );
  }, [list, q]);

  if (!db) return null;

  // ── Fiche compte ──────────────────────────────────────────────────────────
  if (account) {
    const compte = compteOf(db, account);

    if (!compte) {
      return (
        <AppLayout title="CRM · Compte client">
          <style>{COCKPIT_CSS}</style>
          <style>{CRM_CSS}</style>
          <div className="ckpt">
            <div className="ckpt-empty">
              <div className="ic">🔍</div>
              <div className="t">Compte introuvable</div>
              <div className="d">Ce compte client a été supprimé ou n'existe pas.</div>
              <button className="btn btn-primary" onClick={() => navigate('/crm/comptes')}>
                Retour aux comptes
              </button>
            </div>
          </div>
        </AppLayout>
      );
    }

    const st = statsOf(db, compte);
    const premiereEquipe = equipesOfCompte(compte)[0] || null;

    // Écriture directe d'un champ du compte dans le store partagé.
    const setChamp = (k, v) => update((n) => {
      const c = (n.clients || []).find((x) => x.name === account);
      if (c) c[k] = v;
    });

    return (
      <AppLayout title="CRM · Compte client">
        <style>{COCKPIT_CSS}</style>
        <style>{CRM_CSS}</style>
        <div className="ckpt">

          <div className="crm-accbar">
            <button className="acc-back" onClick={() => navigate('/crm/comptes')}>← Comptes</button>
          </div>

          {/* Bandeau d'identité */}
          <div className="acc-header">
            <div className="ah-top">
              <div className="acc-id">
                <span className="aav">{initials(compte.name)}</span>
                <div>
                  <div className="nm">{compte.name}</div>
                  <div className="sub2">
                    {[compte.sector, compte.city].filter(Boolean).join(' · ') || 'Secteur et ville à renseigner'}
                  </div>
                </div>
              </div>
              <div className="ah-actions">
                <button
                  className="btn btn-ghost"
                  onClick={() => navigate(`/crm/perimetre/${encodeURIComponent(compte.name)}`)}
                >
                  Modifier le périmètre →
                </button>
                {premiereEquipe && (
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate(`/crm/fiches/new?account=${encodeURIComponent(compte.name)}&entity=${encodeURIComponent(premiereEquipe.entite)}&team=${encodeURIComponent(premiereEquipe.eq.name)}`)}
                  >
                    + Fiche client
                  </button>
                )}
              </div>
            </div>

            <div className="ah-strip">
              <div className="ah-kpis">
                <div className="acc-stat"><div className="sk">Entités</div><div className="sv">{st.entites}</div></div>
                <div className="acc-stat"><div className="sk">Équipes</div><div className="sv">{st.equipes}</div></div>
                <div className="acc-stat"><div className="sk">Fiches clients</div><div className="sv">{st.fiches}</div></div>
                <div className="acc-stat"><div className="sk">Équipes actives</div><div className="sv win">{st.actives}</div></div>
              </div>
            </div>
          </div>

          {/* Identité du compte */}
          <div className="card crm-card">
            <div className="crm-card-t">Identité du compte</div>
            <div className="crm-grid2">
              <label className="crm-field"><span>Secteur</span>
                <input className="crm-input" value={compte.sector || ''} placeholder="Ex. Banque"
                  onChange={(e) => setChamp('sector', e.target.value)} />
              </label>
              <label className="crm-field"><span>Ville</span>
                <input className="crm-input" value={compte.city || ''} placeholder="Ex. Paris"
                  onChange={(e) => setChamp('city', e.target.value)} />
              </label>
              <label className="crm-field"><span>Contact principal</span>
                <input className="crm-input" value={compte.contact || ''} placeholder="Nom du référent"
                  onChange={(e) => setChamp('contact', e.target.value)} />
              </label>
              <label className="crm-field"><span>Email</span>
                <input className="crm-input" type="email" value={compte.email || ''}
                  onChange={(e) => setChamp('email', e.target.value)} />
              </label>
              <label className="crm-field crm-field-wide"><span>Téléphone</span>
                <input className="crm-input" value={compte.phone || ''}
                  onChange={(e) => setChamp('phone', e.target.value)} />
              </label>
            </div>
          </div>

          {/* Périmètre : entités > équipes > contacts référencés */}
          <div className="acc-sec-head">
            <div className="t">Périmètre</div>
            <div className="c">{st.entites} entité(s) · {st.equipes} équipe(s)</div>
          </div>

          {!st.entites ? (
            <div className="ckpt-empty">
              <div className="ic">🗺️</div>
              <div className="t">Périmètre vide</div>
              <div className="d">Ce compte n'a encore ni entité ni équipe. Cartographiez-le pour pouvoir y rattacher vos contacts.</div>
              <button
                className="btn btn-primary"
                onClick={() => navigate(`/crm/perimetre/${encodeURIComponent(compte.name)}`)}
              >
                Construire le périmètre →
              </button>
            </div>
          ) : (
            entitesOf(compte).map((e) => {
              const eqs = equipesOf(e);
              const nbFichesEnt = eqs.reduce(
                (s, eq) => s + fichesOfTeam(db, compte.name, e.name, eq.name).length, 0
              );
              return (
                <div className="crm-ent" key={e.name}>
                  <div className="crm-ent-h">
                    <span className="crm-ent-ic">🏢</span>
                    <span className="rt-n">{e.name}</span>
                    <span className="crm-ent-meta">{eqs.length} équipe(s) · {nbFichesEnt} fiche(s)</span>
                  </div>

                  {!eqs.length && <div className="crm-team-empty">Aucune équipe dans cette entité.</div>}

                  {eqs.map((eq) => {
                    const fiches = fichesOfTeam(db, compte.name, e.name, eq.name);
                    const s = statusOf(eq);
                    return (
                      <div className="crm-team" key={eq.name}>
                        <div className="crm-team-h">
                          <span className="crm-pill" style={{ background: `${TEAM_STATUS[s].color}22`, color: TEAM_STATUS[s].color }}>
                            {TEAM_STATUS[s].label}
                          </span>
                          <span className="team-name">{eq.name}</span>
                          <span className="sp" />
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => navigate(`/crm/fiches/new?account=${encodeURIComponent(compte.name)}&entity=${encodeURIComponent(e.name)}&team=${encodeURIComponent(eq.name)}`)}
                          >
                            + Fiche client
                          </button>
                        </div>

                        {fiches.length ? (
                          <div className="crm-fiches">
                            {fiches.map((f) => (
                              <button className="crm-fiche-chip" key={f.id} onClick={() => navigate(`/crm/fiches/${f.id}`)}>
                                <span className="crm-av">{initials(fullName(f))}</span>
                                <span className="crm-fiche-nm">{fullName(f)}</span>
                                {f.fonction && <span className="crm-fiche-fn">{f.fonction}</span>}
                                {f.decisionnaire && <span className="crm-deci" title="Décisionnaire">★</span>}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="crm-team-empty">Aucun client référencé dans cette équipe.</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}

          <div className="crm-footbar">
            <button className="btn btn-ghost" onClick={() => navigate('/crm/comptes')}>← Retour aux comptes</button>
          </div>
        </div>
      </AppLayout>
    );
  }

  // ── Liste des comptes ─────────────────────────────────────────────────────

  const onCreate = () => {
    const name = form.name.trim();
    if (!name) { notify('Renseignez le nom du compte'); return; }
    if (list.some((c) => c.name === name)) { notify('Ce compte existe déjà'); return; }
    update((n) => {
      n.clients = n.clients || [];
      n.clients.push({
        name,
        sector: form.sector.trim(),
        city: form.city.trim(),
        contact: '', email: '', phone: '',
        clients: [],
      });
    });
    setForm({ name: '', sector: '', city: '' });
    setCreating(false);
    notify(`Compte « ${name} » créé`);
  };

  return (
    <AppLayout title="CRM · Comptes clients">
      <style>{COCKPIT_CSS}</style>
      <style>{CRM_CSS}</style>
      <div className="ckpt">

        <div className="crm-searchbar">
          <input
            className="crm-input"
            placeholder="Rechercher un compte (nom, secteur, ville)…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button className="btn btn-primary" onClick={() => setCreating((v) => !v)}>
            + Compte client
          </button>
        </div>

        {/* Création d'un compte */}
        {creating && (
          <div className="card crm-card crm-create">
            <div className="crm-card-t">Nouveau compte client</div>
            <div className="crm-grid2">
              <label className="crm-field"><span>Nom du compte</span>
                <input className="crm-input" autoFocus placeholder="Ex. BNP Paribas" value={form.name}
                  onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && onCreate()} />
              </label>
              <label className="crm-field"><span>Secteur</span>
                <input className="crm-input" placeholder="Ex. Banque" value={form.sector}
                  onChange={(e) => setForm((s) => ({ ...s, sector: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && onCreate()} />
              </label>
              <label className="crm-field crm-field-wide"><span>Ville</span>
                <input className="crm-input" placeholder="Ex. Paris" value={form.city}
                  onChange={(e) => setForm((s) => ({ ...s, city: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && onCreate()} />
              </label>
            </div>
            <div className="crm-footbar">
              <span className="sp" />
              <button className="btn btn-ghost" onClick={() => setCreating(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={onCreate}>Créer le compte</button>
            </div>
          </div>
        )}

        {!list.length && (
          <div className="ckpt-empty">
            <div className="ic">🏢</div>
            <div className="t">Aucun compte client</div>
            <div className="d">Le compte est le point de départ du CRM : il porte ses entités, ses équipes et les contacts que vous y suivez.</div>
            <button className="btn btn-primary" onClick={() => setCreating(true)}>+ Compte client</button>
          </div>
        )}

        {!!list.length && !filtered.length && (
          <div className="ckpt-empty">
            <div className="ic">🔍</div>
            <div className="t">Aucun compte ne correspond</div>
            <div className="d">Aucun compte ne correspond à « {q} ». Essayez un autre nom, secteur ou ville.</div>
            <button className="btn btn-ghost" onClick={() => setQ('')}>Effacer la recherche</button>
          </div>
        )}

        <div className="cardsgrid">
          {filtered.map((c) => {
            const st = statsOf(db, c);
            const ents = entitesOf(c);
            return (
              <div
                className="client-card"
                key={c.name}
                onClick={() => navigate(`/crm/comptes/${encodeURIComponent(c.name)}`)}
              >
                <div className="ch">
                  <span className="clogo">{initials(c.name)}</span>
                  <div>
                    <div className="cn">{c.name}</div>
                    <div className="cmeta">
                      {[c.sector, c.city].filter(Boolean).join(' · ') || 'Secteur et ville à renseigner'}
                    </div>
                  </div>
                </div>

                <div className="cgrid">
                  <div className="stat"><div className="sk">Fiches clients</div><div className="sv">{st.fiches}</div></div>
                  <div className="stat"><div className="sk">Entités</div><div className="sv">{st.entites}</div></div>
                  <div className="stat"><div className="sk">Équipes</div><div className="sv">{st.equipes}</div></div>
                  <div className="stat"><div className="sk">Actives</div><div className="sv win">{st.actives}</div></div>
                </div>

                <div className="scl-chips">
                  {ents.slice(0, 3).map((e) => <span className="scl-chip" key={e.name}>{e.name}</span>)}
                  {ents.length > 3 && <span className="scl-chip more">+{ents.length - 3}</span>}
                  {!ents.length && <span className="scl-chip more">Périmètre à construire</span>}
                </div>

                <div className="cfoot">
                  <span>{st.entites} entité(s) · {st.equipes} équipe(s)</span>
                  <span className="crm-open">Ouvrir →</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AppLayout>
  );
}
